import React from 'react';
import styled, { css } from 'styled-components/macro';
import PropTypes from 'prop-types';

import { isPortrait } from '../utils/size';

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    justify-content: space-between;
    position: relative;
    padding: ${props => 12 + props.paddingAdjustment}px 0;
    overflow: hidden;

    input:focus {
        outline: none;
        border: solid 1px #979191;
    }
`;

const InputCss = css`
    appearance: none;
    background-color: transparent;
    border-radius: 2px;
    border: solid 1px ${props => (props.labelIsValid ? '#f1f1ee' : '#ff245e')};
    color: #333;
    font-family: Averta;
    font-size: ${() => (isPortrait() ? 16 : 14)}px;
    line-height: ${() => (isPortrait() ? 20 : 18)}px;
    height: 36px;
    overflow: hidden;
    padding: 8px;
    z-index: 0;

    &::placeholder {
        opacity: 0;
        display: none;
    }

    @media (max-width: 30em) {
        margin-left: 0;
        margin-right: 0;
    }
`;

const Input = styled.input`
    ${InputCss}
`;
const CustomInputContainer = styled.div`
    ${InputCss}
`;

const LabelContainer = styled.div`
    display: flex;
    justify-content: space-between;
`;

const Label = styled.label`
    border-radius: 5px;
    font-family: Averta;
    left: 5px;
    letter-spacing: 1px;
    line-height: 1.5;
    padding: 0 5px;
    pointer-events: none;
    position: absolute;
    background-color: ${props =>
        props.active || (!props.active && props.value)
            ? props.labelBackgroundColor
                ? props.labelBackgroundColor
                : `white`
            : `transparent`};
    background-color: ${props => props.isCustomInput && `white`};
    top: ${props => (props.default ? 20 : 6) + props.paddingAdjustment}px;
    font-size: ${props => (props.default ? 14 : 8)}px;
    font-weight: ${props => (props.default ? `400` : `600`)};
    text-transform: ${props => (props.default ? `capitalize` : `uppercase`)};
    color: ${props => (props.labelIsValid ? `#979191` : `#ff245e`)};
    transition: all 250ms ease;
    white-space: nowrap;
    z-index: 2;
`;

const LabelInfo = styled(Label)`
    text-transform: none;
    letter-spacing: 0;
    margin-top: 6px;
    margin-bottom: 0;
    font-weight: 500;
    font-size: 8.5px;
    line-height: 1.4;
`;

class InputGroup extends React.Component {
    constructor(props) {
        super(props);
        this.textInput = null;
        this.setTextInputRef = element => (this.textInput = element);
        this.focusTextInput = () => {
            if (this.textInput) this.textInput.focus();
        };
        this.state = {
            customFocused: false,
            customHasValue: false,
            customValid: true
        };
    }

    componentDidMount() {
        if (this.props.autoFocus) {
            this.focusTextInput();
        }
    }

    clearFormLevelSubmissionErrors() {
        if (typeof this.props.clearSubmitErrors === 'function' && this.props.input.name) {
            this.props.clearSubmitErrors(this.props.input.name);
        }
    }

    render() {
        const defaultState = this.props.customInput
            ? !this.state.customFocused && !this.state.customHasValue
            : !this.props.meta.active &&
              !this.props.meta.valid &&
              (this.props.meta.pristine || this.props.input.value === '');
        const selected = this.props.customInput
            ? this.state.customFocused
            : this.props.meta.active;
        const error = this.props.customInput
            ? null
            : this.props.submissionError ||
              (this.state.touched &&
                  this.props.meta.invalid &&
                  this.props.meta.touched &&
                  this.props.input.value !== '');
        const labelIsValid = this.props.customInput
            ? !this.props.submissionError &&
              (defaultState || this.state.customValid || this.state.customFocused)
            : !this.props.submissionError &&
              (defaultState || this.props.meta.active || this.props.meta.valid);
        const CustomInput = this.props.customInput;

        return (
            <Container
                className={this.props.class}
                paddingAdjustment={this.props.paddingAdjustment || 0}
            >
                <LabelContainer>
                    <Label
                        isCustomInput={!!this.props.customInput}
                        {...this.props.input}
                        {...this.props.meta}
                        {...this.state}
                        default={defaultState}
                        labelBackgroundColor={this.props.labelBackgroundColor}
                        labelIsValid={labelIsValid}
                        paddingAdjustment={this.props.paddingAdjustment || 0}
                    >
                        {error && !selected ? this.props.meta.error : this.props.label}
                    </Label>
                </LabelContainer>
                {this.props.customInput ? (
                    <CustomInputContainer
                        {...this.props.input}
                        {...this.props.meta}
                        {...this.state}
                        default={defaultState}
                        labelIsValid={labelIsValid}
                    >
                        <CustomInput
                            className={this.props.className}
                            onChange={({ empty, error, complete }) => {
                                const isValid = !empty && !error && complete;
                                if (this.props.onValidityChange) {
                                    this.props.onValidityChange(isValid);
                                }
                                this.setState({
                                    customHasValue: !empty,
                                    customValid: isValid
                                });
                            }}
                            onFocus={() => {
                                this.setState({ customFocused: true });
                                this.clearFormLevelSubmissionErrors();
                            }}
                            onBlur={() => this.setState({ customFocused: false })}
                            style={{
                                base: {
                                    fontFamily: 'Averta',
                                    fontSize: '14px'
                                },
                                invalid: { color: '#333' }
                            }}
                        />
                    </CustomInputContainer>
                ) : (
                    <Input
                        {...this.props.input}
                        {...this.props.meta}
                        onFocus={() => {
                            this.props.input.onFocus();
                            this.clearFormLevelSubmissionErrors();
                        }}
                        className={this.props.className}
                        default={defaultState}
                        type={this.props.type}
                        maxLength={this.props.maxLength}
                        autoComplete={this.props.autocomplete}
                        size="10"
                        readOnly={this.props.readOnly}
                        ref={this.setTextInputRef}
                        labelIsValid={labelIsValid}
                    />
                )}

                {this.props.infoText && <LabelInfo>{this.props.infoText}</LabelInfo>}
            </Container>
        );
    }
}

InputGroup.propTypes = {
    label: PropTypes.string.isRequired,
    labelBackgroundColor: PropTypes.string,
    type: PropTypes.string.isRequired,
    input: PropTypes.object.isRequired,
    meta: PropTypes.object.isRequired,
    primary: PropTypes.bool,
    autoFocus: PropTypes.bool,
    maxLength: PropTypes.number,
    autoFocusTimeout: PropTypes.number,
    placeholder: PropTypes.string,
    autocomplete: PropTypes.string,
    inputColor: PropTypes.string,
    className: PropTypes.string,
    readOnly: PropTypes.bool,
    submissionError: PropTypes.bool,
    infoText: PropTypes.string,
    class: PropTypes.string,
    customInput: PropTypes.func,
    clearSubmitErrors: PropTypes.func,
    paddingAdjustment: PropTypes.number,
    onValidityChange: PropTypes.func
};

export default InputGroup;
